import scheduleNextPaint from '@/helpers/scheduleNextPaint';
import perfDebug from '@/helpers/perfDebug';

/**
 * Обходит array-like (массив, NodeList) чанками, между чанками — scheduleFrame (по умолчанию rAF).
 * Снижает длину задач на main thread (TBT).
 *
 * @param {ArrayLike<*>} arrayLike
 * @param {{ chunkSize?: number, chunkTimeBudgetMs?: number, scheduleFrame?: (cb: () => void) => void, perfLabel?: string }} options
 * @param {(item: *, index: number) => void} iteratee
 * @return {Promise<void>}
 */
export default function forEachInRafChunks(arrayLike, options, iteratee) {
  const opts = options || {};
  const chunkSize = opts.chunkSize > 0 ? opts.chunkSize : 1;
  const chunkTimeBudgetMs =
    typeof opts.chunkTimeBudgetMs === 'number' && opts.chunkTimeBudgetMs > 0 ? opts.chunkTimeBudgetMs : 0;
  const scheduleFrame = opts.scheduleFrame || scheduleNextPaint;
  const length = arrayLike == null ? 0 : arrayLike.length;
  const perfLabel = opts.perfLabel;
  const perf = perfDebug.isEnabled() && perfLabel;

  return new Promise(function(resolve) {
    if (!length) {
      resolve();

      return;
    }

    let index = 0;
    let chunkIndex = 0;

    function runChunk() {
      const chunkStart = index;
      const targetEnd = Math.min(index + chunkSize, length);
      const t0 = perf ? perfDebug.now() : 0;
      const deadline = chunkTimeBudgetMs > 0 ? t0 + chunkTimeBudgetMs : Infinity;

      let firstInSlice = true;
      while (index < targetEnd && index < length) {
        if (!firstInSlice && chunkTimeBudgetMs > 0 && perfDebug.now() >= deadline) {
          break;
        }
        iteratee(arrayLike[index], index);
        index += 1;
        firstInSlice = false;
      }

      const processedEnd = index;
      const yieldedByTime =
        chunkTimeBudgetMs > 0 && processedEnd < targetEnd && processedEnd < length;

      if (perf) {
        const t1 = perfDebug.now();
        const ms = t1 - t0;
        const detail = {
          chunkIndex,
          items: processedEnd - chunkStart,
          fromIndex: chunkStart,
          toIndex: processedEnd > 0 ? processedEnd - 1 : chunkStart,
          yieldedByTime,
          chunkTimeBudgetMs: chunkTimeBudgetMs || undefined,
        };
        perfDebug.log(`${perfLabel}:chunk`, detail, ms);
        if (yieldedByTime) {
          perfDebug.log(`${perfLabel}:chunk:timeBudget`, {
            chunkIndex,
            deadlineMs: chunkTimeBudgetMs,
            processedInSlice: processedEnd - chunkStart,
            nextIndex: processedEnd,
          });
        }
        perfDebug.ensureSummary().chunks.push({ label: perfLabel, ...detail, ms });
        perfDebug.measureRange(`${perfLabel}:chunk:${chunkIndex}`, t0, t1);
        const slowMs = typeof window !== 'undefined' && window.__INSALES_COMMON_PERF_SLOW_CHUNK_MS__;
        const threshold = typeof slowMs === 'number' && slowMs > 0 ? slowMs : 45;
        if (ms >= threshold) {
          perfDebug.logWithStack(
            `${perfLabel}:chunk:slow`,
            { ...detail, ms, threshold },
            24,
          );
        }
      }
      chunkIndex += 1;

      if (index < length) {
        scheduleFrame(runChunk);
      } else {
        resolve();
      }
    }

    runChunk();
  });
}
